import React from 'react';
import { Box, HStack, Text, VStack } from '@chakra-ui/react';
import { type SentInvitationData } from '@/hooks/sharing/useShareForm.ts';
import { FiCheck } from 'react-icons/fi';
import { format } from 'date-fns';

interface RecentInvitesListProps {
	items: SentInvitationData[];
}

/**
 * Displays the list of people just connected inside the Share dialog
 */
const RecentInvitesList: React.FC<RecentInvitesListProps> = ({ items }) => {
	if (!items?.length) return null;

	const visible = items.slice(0, 3);
	const remaining = items.length - 3;

	return (
		<Box
			bg="green.50"
			borderRadius="2xl"
			px={3}
			py={4}
			css={{
				animation: 'fadeIn 0.2s ease-out forwards',
				'@keyframes fadeIn': {
					from: { opacity: 0 },
					to: { opacity: 1 },
				},
			}}
		>
			<Text
				textStyle="micro"
				fontWeight="semibold"
				color="green.600"
				textTransform="uppercase"
				letterSpacing="0.5px"
				mb={2}
			>
				Connected {items.length} {items.length > 1 ? 'people' : 'person'}
			</Text>
			<VStack gap={2} align="stretch">
				{visible.map((inv, idx) => (
					<HStack key={`${inv.email}-${idx}`} gap={2} textStyle="micro" align="center">
						<Box
							flexShrink={0}
							boxSize="20px"
							display="flex"
							alignItems="center"
							justifyContent="center"
							bg="green.600"
							color="white"
							borderRadius="full"
						>
							<Box as={FiCheck} boxSize="12px" />
						</Box>
						<Text
							color="primary.fixed"
							flex={1}
							overflow="hidden"
							textOverflow="ellipsis"
							whiteSpace="nowrap"
						>
							{inv.email}
						</Text>
						<Text color="primary/50" textStyle="micro" flexShrink={0}>
							{inv.expiresAt ? format(inv.expiresAt, 'dd MMM') : 'indefinite'}
						</Text>
					</HStack>
				))}
				{remaining > 0 && (
					<Text textStyle="micro" color="fg.muted">
						+{remaining} more
					</Text>
				)}
			</VStack>
		</Box>
	);
};

export default RecentInvitesList;
